import { useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { completeMissionByAction } from '@/services/pointsService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Helper function to get mission by name
async function getMissionByName(name: string) {
  const { data: mission, error } = await supabase
    .from('missions')
    .select('id')
    .eq('name', name)
    .eq('is_active', true)
    .single();
  
  if (error) {
    console.error(`Error fetching mission ${name}:`, error);
    return null;
  }
  
  return mission;
}

export function useMissions() {
  const { user } = useAuth();

  // Function to complete a mission automatically
  const completeMissionAction = useCallback(async (action: string, targetId?: string) => {
    if (!user?.id) return false;

    try {
      const success = await completeMissionByAction(user.id, action, targetId);
      if (success) {
        // Get mission name for toast
        const { data: mission } = await supabase
          .from('missions')
          .select('name, title, coins_reward')
          .eq('name', action)
          .eq('is_active', true)
          .single();
        
        if (mission) {
          toast.success(`Mission completed: ${mission.title}`, {
            description: `+${mission.coins_reward} coins earned!`,
          });
        }
      }
      return success;
    } catch (error) {
      console.error(`Error completing mission for action ${action}:`, error);
      return false;
    }
  }, [user?.id]);

  // Function to handle daily login action - check if mission can be completed
  const handleDailyLogin = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // First, get the mission ID
      const mission = await getMissionByName('daily_login');
      
      if (!mission) return;
      
      // Check if daily_login mission was completed today
      const today = new Date().toISOString().split('T')[0];
      
      // Get all missions for this player/mission combination
      const { data: allMissions, error: checkError } = await supabase
        .from('player_missions')
        .select('id, completed_at, completion_date')
        .eq('player_id', user.id)
        .eq('mission_id', mission.id);
      
      if (checkError) return;
      
      // Check if any mission was completed today using the completion_date column
      const existingMission = allMissions?.find(mission => {
        const missionDate = mission.completion_date || new Date(mission.completed_at).toISOString().split('T')[0];
        return missionDate === today;
      });

      // If not completed today, complete it
      if (!existingMission) {
        await completeMissionAction('daily_login');
      }
    } catch (error) {
      console.error('Error checking daily login mission:', error);
    }
  }, [user?.id, completeMissionAction]);

  // Function to handle game like
  const handleLikeGame = useCallback((gameId: string) => {
    completeMissionAction('like_game', gameId);
  }, [completeMissionAction]);

  // Function to handle developer like
  const handleLikeDeveloper = useCallback((developerId: string) => {
    completeMissionAction('like_developer', developerId);
  }, [completeMissionAction]);

  // Function to handle adding to favorites
  const handleAddFavorite = useCallback((gameId: string) => {
    completeMissionAction('add_favorite', gameId);
  }, [completeMissionAction]);

  // Function to handle game page visit
  const handleVisitGamePage = useCallback((gameId: string) => {
    completeMissionAction('visit_game_page', gameId);
  }, [completeMissionAction]);

  // Function to handle friend invitation
  const handleInviteFriend = useCallback(() => {
    completeMissionAction('invite_friend');
  }, [completeMissionAction]);

  // Function to handle signup
  const handleSignup = useCallback(() => {
    completeMissionAction('first_signup');
  }, [completeMissionAction]);

  return {
    completeMissionAction,
    handleDailyLogin,
    handleLikeGame,
    handleLikeDeveloper,
    handleAddFavorite,
    handleVisitGamePage,
    handleInviteFriend,
    handleSignup
  };
}
